import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Switch,
  Button,
  Box,
  Collapse,
  Divider,
  Chip,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
} from '@mui/material';

// Default downtime costs by facility type ($/hour)
const FACILITY_DEFAULTS = {
  commercial: 100,
  retail: 500,
  manufacturing: 5000,
  healthcare: 10000,
  datacenter: 50000,
  pharmaceutical: 25000,
  financial: 100000,
};

const FACILITY_LABELS = {
  commercial: 'Standard Commercial',
  retail: 'Retail / Hospitality',
  manufacturing: 'Manufacturing',
  healthcare: 'Healthcare',
  datacenter: 'Data Center',
  pharmaceutical: 'Pharmaceutical',
  financial: 'Financial Services',
};
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
import { smoothSlideIn, springEasing } from '../theme/animations';

/**
 * CompanySetup - Financial Configuration Form
 *
 * Collects company-specific financial parameters for CFO metric calculations.
 * Features progressive disclosure (simple → advanced mode) and intelligent defaults.
 */
export default function CompanySetup({ onComplete }) {
  const [companyType, setCompanyType] = useState('private');
  const [annualRevenue, setAnnualRevenue] = useState('');
  const [advancedMode, setAdvancedMode] = useState(true);

  // Blended electricity rate (all-in cost per kWh)
  const [blendedElectricityRate, setBlendedElectricityRate] = useState('0.10');

  // Equipment replacement cycle (years)
  const [equipmentReplacementCycle, setEquipmentReplacementCycle] = useState('10');

  // Equipment cost per ton (for capital deferment analysis)
  const [equipmentCostPerTon, setEquipmentCostPerTon] = useState('');

  // Facility type for mission-critical protection value
  const [facilityType, setFacilityType] = useState('commercial');
  const [downtimeCost, setDowntimeCost] = useState('100');

  // Advanced financial parameters
  const [ebitdaMultiple, setEbitdaMultiple] = useState('8');
  const [profitMargin, setProfitMargin] = useState('5');
  const [wacc, setWacc] = useState('10');
  const [taxRate, setTaxRate] = useState('25');

  // REIT-specific parameters
  const [propertyValue, setPropertyValue] = useState('');
  const [capRate, setCapRate] = useState('8');
  const [squareFootage, setSquareFootage] = useState('');
  const [totalOpEx, setTotalOpEx] = useState('');
  const [annualDebtService, setAnnualDebtService] = useState('');

  // Public Company-specific parameters
  const [sharesOutstanding, setSharesOutstanding] = useState('');
  const [peRatio, setPeRatio] = useState('20');
  const [carbonPrice, setCarbonPrice] = useState('50');

  const [errors, setErrors] = useState({});

  // Validation
  const validateForm = () => {
    const newErrors = {};

    // Validate percentages (0-100)
    if (advancedMode) {
      const percentFields = [
        { value: profitMargin, name: 'profitMargin', label: 'Profit Margin' },
        { value: wacc, name: 'wacc', label: 'WACC' },
        { value: taxRate, name: 'taxRate', label: 'Tax Rate' },
      ];

      if (companyType === 'reit') {
        percentFields.push({ value: capRate, name: 'capRate', label: 'Cap Rate' });
      }

      percentFields.forEach(({ value, name, label }) => {
        const num = parseFloat(value);
        if (isNaN(num) || num < 0 || num > 100) {
          newErrors[name] = `${label} must be between 0-100%`;
        }
      });

      // Validate EBITDA multiple (positive number)
      const multiple = parseFloat(ebitdaMultiple);
      if (isNaN(multiple) || multiple <= 0) {
        newErrors.ebitdaMultiple = 'EBITDA multiple must be positive';
      }
    }

    // Validate optional revenue (if provided, must be positive)
    if (annualRevenue && annualRevenue !== '') {
      const revenue = parseFloat(annualRevenue);
      if (isNaN(revenue) || revenue <= 0) {
        newErrors.annualRevenue = 'Revenue must be a positive number';
      }
    }

    // Validate blended electricity rate
    if (blendedElectricityRate && blendedElectricityRate !== '') {
      const rate = parseFloat(blendedElectricityRate);
      if (isNaN(rate) || rate < 0.01 || rate > 1.00) {
        newErrors.blendedElectricityRate = 'Rate must be between $0.01 and $1.00 per kWh';
      }
    }

    // Validate equipment replacement cycle
    if (equipmentReplacementCycle && equipmentReplacementCycle !== '') {
      const cycle = parseInt(equipmentReplacementCycle);
      if (isNaN(cycle) || cycle < 5 || cycle > 30) {
        newErrors.equipmentReplacementCycle = 'Replacement cycle must be between 5 and 30 years';
      }
    }

    // Validate downtime cost
    if (downtimeCost && downtimeCost !== '') {
      const cost = parseFloat(downtimeCost);
      if (isNaN(cost) || cost < 0) {
        newErrors.downtimeCost = 'Downtime cost must be a non-negative number';
      }
    }

    // Validate REIT fields (if provided)
    if (companyType === 'reit') {
      if (propertyValue && propertyValue !== '') {
        const value = parseFloat(propertyValue);
        if (isNaN(value) || value <= 0) {
          newErrors.propertyValue = 'Property value must be positive';
        }
      }
      if (squareFootage && squareFootage !== '') {
        const sf = parseFloat(squareFootage);
        if (isNaN(sf) || sf <= 0) {
          newErrors.squareFootage = 'Square footage must be positive';
        }
      }
      if (totalOpEx && totalOpEx !== '') {
        const opex = parseFloat(totalOpEx);
        if (isNaN(opex) || opex <= 0) {
          newErrors.totalOpEx = 'Operating expenses must be positive';
        }
      }
      if (annualDebtService && annualDebtService !== '') {
        const debt = parseFloat(annualDebtService);
        if (isNaN(debt) || debt <= 0) {
          newErrors.annualDebtService = 'Debt service must be positive';
        }
      }
    }

    // Validate Public Company fields (if provided)
    if (companyType === 'public') {
      if (sharesOutstanding && sharesOutstanding !== '') {
        const shares = parseFloat(sharesOutstanding);
        if (isNaN(shares) || shares <= 0) {
          newErrors.sharesOutstanding = 'Shares outstanding must be positive';
        }
      }
      if (totalOpEx && totalOpEx !== '') {
        const opex = parseFloat(totalOpEx);
        if (isNaN(opex) || opex <= 0) {
          newErrors.totalOpEx = 'Operating expenses must be positive';
        }
      }
      if (peRatio && peRatio !== '') {
        const pe = parseFloat(peRatio);
        if (isNaN(pe) || pe <= 0) {
          newErrors.peRatio = 'P/E ratio must be positive';
        }
      }
      if (carbonPrice && carbonPrice !== '') {
        const price = parseFloat(carbonPrice);
        if (isNaN(price) || price < 0) {
          newErrors.carbonPrice = 'Carbon price must be non-negative';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const config = {
      companyType,
      annualRevenue: annualRevenue ? parseFloat(annualRevenue) : null,
      blendedElectricityRate: blendedElectricityRate ? parseFloat(blendedElectricityRate) : 0.10,
      equipmentReplacementCycle: equipmentReplacementCycle ? parseInt(equipmentReplacementCycle) : 10,
      equipmentCostPerTon: equipmentCostPerTon ? parseFloat(equipmentCostPerTon) : null,
      facilityType,
      customDowntimeCost: downtimeCost ? parseFloat(downtimeCost) : FACILITY_DEFAULTS[facilityType],
    };

    if (advancedMode) {
      config.ebitdaMultiple = parseFloat(ebitdaMultiple);
      config.profitMargin = parseFloat(profitMargin) / 100;
      config.wacc = parseFloat(wacc) / 100;
      config.taxRate = parseFloat(taxRate) / 100;

      if (companyType === 'reit') {
        config.propertyValue = propertyValue ? parseFloat(propertyValue) : null;
        config.capRate = parseFloat(capRate) / 100;
        config.squareFootage = squareFootage ? parseFloat(squareFootage) : null;
        config.totalOpEx = totalOpEx ? parseFloat(totalOpEx) : null;
        config.annualDebtService = annualDebtService ? parseFloat(annualDebtService) : null;
      }

      if (companyType === 'public') {
        config.sharesOutstanding = sharesOutstanding ? parseFloat(sharesOutstanding) : null;
        config.totalOpEx = totalOpEx ? parseFloat(totalOpEx) : null;
        config.peRatio = peRatio ? parseFloat(peRatio) : 20;
        config.carbonPrice = carbonPrice ? parseFloat(carbonPrice) : 50;
      }
    }

    onComplete(config);
  };

  return (
    <Card
      sx={{
        maxWidth: 800,
        mx: 'auto',
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(26, 31, 37, 1) 0%, rgba(20, 20, 20, 1) 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(160, 206, 78, 0.08)'
            : '0 8px 32px rgba(0, 0, 0, 0.08)',
      }}
    >
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.02em',
              }}
            >
              Company Information
            </Typography>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            Configure your financial parameters to calculate executive-level ROI metrics
          </Typography>

          {/* Privacy Notice - Top */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 2,
              p: 1.5,
              borderRadius: 1.5,
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(160, 206, 78, 0.08)'
                  : 'rgba(124, 179, 66, 0.06)',
              border: '1px solid',
              borderColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(160, 206, 78, 0.2)'
                  : 'rgba(124, 179, 66, 0.15)',
            }}
          >
            <LockIcon sx={{ fontSize: 18, color: 'primary.main', opacity: 0.8 }} />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.4,
              }}
            >
              <strong>Your data stays private</strong> — all calculations run locally in your browser.
              We don't store, transmit, or have access to any information you enter.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Company Type Selection */}
        <FormControl
          component="fieldset"
          sx={{
            width: '100%',
            mb: 3,
            animation: `${smoothSlideIn} 0.5s ease-out`,
          }}
        >
          <FormLabel
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 1.5,
              fontSize: '0.95rem',
            }}
          >
            Company Type
          </FormLabel>
          <RadioGroup
            value={companyType}
            onChange={(e) => setCompanyType(e.target.value)}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
              }}
            >
              {[
                { value: 'private', label: 'Private Company', desc: 'Enterprise value metrics' },
                { value: 'public', label: 'Public Company', desc: 'Market cap impact' },
                { value: 'reit', label: 'REIT / Property', desc: 'NOI & FFO metrics' },
              ].map(({ value, label, desc }) => (
                <Box
                  key={value}
                  sx={{
                    flex: 1,
                    position: 'relative',
                    border: '3px solid',
                    borderColor: companyType === value ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 2,
                    cursor: 'pointer',
                    transition: `all 0.3s ${springEasing}`,
                    background: (theme) =>
                      companyType === value
                        ? theme.palette.mode === 'dark'
                          ? `${theme.palette.primary.main}12`
                          : `${theme.palette.primary.main}08`
                        : 'transparent',
                    '&:hover': {
                      borderColor: 'primary.light',
                      background: (theme) =>
                        theme.palette.mode === 'dark'
                          ? `${theme.palette.primary.main}08`
                          : `${theme.palette.primary.main}04`,
                      transform: 'translateY(-2px)',
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 4px 12px rgba(160, 206, 78, 0.12)'
                          : '0 4px 12px rgba(124, 179, 66, 0.08)',
                    },
                  }}
                  onClick={() => setCompanyType(value)}
                >
                  <FormControlLabel
                    value={value}
                    control={<Radio sx={{ position: 'absolute', top: 8, right: 8 }} />}
                    label={
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {desc}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </Box>
              ))}
            </Box>
          </RadioGroup>
        </FormControl>

        {/* Annual Revenue (Optional) */}
        <TextField
          fullWidth
          label="Annual Revenue (Optional)"
          type="number"
          value={annualRevenue}
          onChange={(e) => setAnnualRevenue(e.target.value)}
          error={!!errors.annualRevenue}
          helperText={errors.annualRevenue || 'For revenue growth comparison metrics'}
          placeholder="50000000"
          InputProps={{
            startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>,
          }}
          sx={{
            mb: 3,
            animation: `${smoothSlideIn} 0.6s ease-out`,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />

        {/* Blended Electricity Rate */}
        <TextField
          fullWidth
          label="Blended Electricity Cost"
          type="number"
          value={blendedElectricityRate}
          onChange={(e) => setBlendedElectricityRate(e.target.value)}
          error={!!errors.blendedElectricityRate}
          helperText={errors.blendedElectricityRate || 'All-in cost per kWh averaged (includes demand charges, taxes, fees)'}
          placeholder="0.10"
          inputProps={{ step: 0.01, min: 0.01, max: 1.00 }}
          InputProps={{
            startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>,
            endAdornment: <Typography sx={{ ml: 1, color: 'text.secondary' }}>/kWh</Typography>,
          }}
          sx={{
            mb: 3,
            animation: `${smoothSlideIn} 0.65s ease-out`,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />

        {/* Equipment Replacement Cycle */}
        <TextField
          fullWidth
          label="Equipment Replacement Cycle"
          type="number"
          value={equipmentReplacementCycle}
          onChange={(e) => setEquipmentReplacementCycle(e.target.value)}
          error={!!errors.equipmentReplacementCycle}
          helperText={errors.equipmentReplacementCycle || 'How often you replace HVAC equipment (affects life extension value)'}
          placeholder="10"
          inputProps={{ step: 1, min: 5, max: 30 }}
          InputProps={{
            endAdornment: <Typography sx={{ ml: 1, color: 'text.secondary' }}>years</Typography>,
          }}
          sx={{
            mb: 3,
            animation: `${smoothSlideIn} 0.7s ease-out`,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />

        {/* Equipment Cost Per Ton - For Capital Deferment Analysis */}
        <TextField
          fullWidth
          label="Equipment Replacement Cost per Ton"
          type="number"
          value={equipmentCostPerTon}
          onChange={(e) => setEquipmentCostPerTon(e.target.value)}
          helperText="Average replacement cost per ton of cooling capacity (optional - enables capital deferment analysis)"
          placeholder="1000"
          inputProps={{ step: 100, min: 500, max: 5000 }}
          InputProps={{
            startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
            endAdornment: <Typography sx={{ ml: 1, color: 'text.secondary' }}>/ton</Typography>,
          }}
          sx={{
            mb: 3,
            animation: `${smoothSlideIn} 0.72s ease-out`,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />

        {/* Building Type - Mission-Critical Protection */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 3,
            animation: `${smoothSlideIn} 0.75s ease-out`,
          }}
        >
          <FormControl fullWidth>
            <InputLabel>Building Type</InputLabel>
            <Select
              value={facilityType}
              label="Building Type"
              onChange={(e) => {
                const newType = e.target.value;
                setFacilityType(newType);
                // Auto-populate with the default value for this type
                setDowntimeCost(String(FACILITY_DEFAULTS[newType]));
              }}
            >
              <MenuItem value="commercial">Standard Commercial</MenuItem>
              <MenuItem value="retail">Retail / Hospitality</MenuItem>
              <MenuItem value="manufacturing">Manufacturing</MenuItem>
              <MenuItem value="healthcare">Healthcare</MenuItem>
              <MenuItem value="datacenter">Data Center</MenuItem>
              <MenuItem value="pharmaceutical">Pharmaceutical</MenuItem>
              <MenuItem value="financial">Financial Services</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Downtime Cost"
            type="number"
            value={downtimeCost}
            onChange={(e) => setDowntimeCost(e.target.value)}
            error={!!errors.downtimeCost}
            helperText={errors.downtimeCost || `Default for ${FACILITY_LABELS[facilityType]}. Edit if yours differs.`}
            inputProps={{ step: 100, min: 0 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              endAdornment: <InputAdornment position="end">/hr</InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Advanced Mode Toggle */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderRadius: 2,
            background: (theme) =>
              advancedMode
                ? theme.palette.mode === 'dark'
                  ? `${theme.palette.primary.main}10`
                  : `${theme.palette.primary.main}06`
                : 'action.hover',
            border: '2px solid',
            borderColor: advancedMode ? 'primary.main' : 'divider',
            transition: `all 0.3s ${springEasing}`,
            animation: `${smoothSlideIn} 0.7s ease-out`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SettingsIcon color={advancedMode ? 'primary' : 'action'} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Advanced Financial Analysis
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Customize EBITDA multiple, margins, and cost of capital
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={advancedMode}
            onChange={(e) => setAdvancedMode(e.target.checked)}
            color="primary"
          />
        </Box>

        {/* Advanced Fields */}
        <Collapse in={advancedMode}>
          <Box sx={{ mt: 3 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Standard Industry Defaults:</strong> 8x EBITDA, 5% margin, 10% WACC, 25% tax
              </Typography>
            </Alert>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <TextField
                label="EBITDA Multiple"
                type="number"
                value={ebitdaMultiple}
                onChange={(e) => setEbitdaMultiple(e.target.value)}
                error={!!errors.ebitdaMultiple}
                helperText={errors.ebitdaMultiple || 'Industry valuation multiple'}
                inputProps={{ step: 0.5, min: 0 }}
                InputProps={{
                  endAdornment: <Typography sx={{ ml: 1, color: 'text.secondary' }}>×</Typography>,
                }}
              />

              <TextField
                label="Net Profit Margin"
                type="number"
                value={profitMargin}
                onChange={(e) => setProfitMargin(e.target.value)}
                error={!!errors.profitMargin}
                helperText={errors.profitMargin || 'After-tax profit margin'}
                inputProps={{ step: 0.1, min: 0, max: 100 }}
                InputProps={{
                  endAdornment: <Typography sx={{ ml: 1, color: 'text.secondary' }}>%</Typography>,
                }}
              />

              <TextField
                label="WACC (Cost of Capital)"
                type="number"
                value={wacc}
                onChange={(e) => setWacc(e.target.value)}
                error={!!errors.wacc}
                helperText={errors.wacc || 'Weighted average cost of capital'}
                inputProps={{ step: 0.1, min: 0, max: 100 }}
                InputProps={{
                  endAdornment: <Typography sx={{ ml: 1, color: 'text.secondary' }}>%</Typography>,
                }}
              />

              <TextField
                label="Effective Tax Rate"
                type="number"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                error={!!errors.taxRate}
                helperText={errors.taxRate || 'Corporate tax rate'}
                inputProps={{ step: 0.1, min: 0, max: 100 }}
                InputProps={{
                  endAdornment: <Typography sx={{ ml: 1, color: 'text.secondary' }}>%</Typography>,
                }}
              />
            </Box>

            {/* REIT-Specific Fields */}
            {companyType === 'reit' && (
              <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                <Chip
                  label="REIT Metrics"
                  size="small"
                  color="primary"
                  sx={{ mb: 2 }}
                />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Property Value (Optional)"
                    type="number"
                    value={propertyValue}
                    onChange={(e) => setPropertyValue(e.target.value)}
                    error={!!errors.propertyValue}
                    helperText={errors.propertyValue || 'Current property valuation'}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>,
                    }}
                  />

                  <TextField
                    label="Capitalization Rate"
                    type="number"
                    value={capRate}
                    onChange={(e) => setCapRate(e.target.value)}
                    error={!!errors.capRate}
                    helperText={errors.capRate || 'NOI / Property Value'}
                    inputProps={{ step: 0.1, min: 0, max: 100 }}
                    InputProps={{
                      endAdornment: <Typography sx={{ ml: 1, color: 'text.secondary' }}>%</Typography>,
                    }}
                  />

                  <TextField
                    label="Square Footage (Optional)"
                    type="number"
                    value={squareFootage}
                    onChange={(e) => setSquareFootage(e.target.value)}
                    error={!!errors.squareFootage}
                    helperText={errors.squareFootage || 'Total rentable SF for NOI/SF'}
                    InputProps={{
                      endAdornment: <Typography sx={{ ml: 1, color: 'text.secondary' }}>SF</Typography>,
                    }}
                  />

                  <TextField
                    label="Total Operating Expenses (Optional)"
                    type="number"
                    value={totalOpEx}
                    onChange={(e) => setTotalOpEx(e.target.value)}
                    error={!!errors.totalOpEx}
                    helperText={errors.totalOpEx || 'Annual OpEx for ratio calculation'}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>,
                    }}
                  />

                  <TextField
                    label="Annual Debt Service (Optional)"
                    type="number"
                    value={annualDebtService}
                    onChange={(e) => setAnnualDebtService(e.target.value)}
                    error={!!errors.annualDebtService}
                    helperText={errors.annualDebtService || 'For DSCR impact calculation'}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>,
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Public Company-Specific Fields */}
            {companyType === 'public' && (
              <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                <Chip
                  label="Public Company Metrics"
                  size="small"
                  color="primary"
                  sx={{ mb: 2 }}
                />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Shares Outstanding (Optional)"
                    type="number"
                    value={sharesOutstanding}
                    onChange={(e) => setSharesOutstanding(e.target.value)}
                    error={!!errors.sharesOutstanding}
                    helperText={errors.sharesOutstanding || 'For EPS impact calculation'}
                    placeholder="100000000"
                  />

                  <TextField
                    label="Total Operating Expenses (Optional)"
                    type="number"
                    value={totalOpEx}
                    onChange={(e) => setTotalOpEx(e.target.value)}
                    error={!!errors.totalOpEx}
                    helperText={errors.totalOpEx || 'For operating margin calculation'}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>,
                    }}
                  />

                  <TextField
                    label="P/E Ratio"
                    type="number"
                    value={peRatio}
                    onChange={(e) => setPeRatio(e.target.value)}
                    error={!!errors.peRatio}
                    helperText={errors.peRatio || 'For valuation sensitivity'}
                    inputProps={{ step: 0.5, min: 0 }}
                    InputProps={{
                      endAdornment: <Typography sx={{ ml: 1, color: 'text.secondary' }}>×</Typography>,
                    }}
                  />

                  <TextField
                    label="Carbon Price Assumption"
                    type="number"
                    value={carbonPrice}
                    onChange={(e) => setCarbonPrice(e.target.value)}
                    error={!!errors.carbonPrice}
                    helperText={errors.carbonPrice || 'For carbon cost avoidance'}
                    inputProps={{ step: 5, min: 0 }}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>,
                      endAdornment: <Typography sx={{ ml: 1, color: 'text.secondary' }}>/ton</Typography>,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>

        {/* Privacy Notice - Bottom (reinforcement) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            mt: 4,
            mb: 1.5,
          }}
        >
          <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontSize: '0.7rem' }}
          >
            100% private — nothing leaves your browser
          </Typography>
        </Box>

        {/* Submit Button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          onClick={handleSubmit}
          sx={{
            py: 1.5,
            fontWeight: 700,
            fontSize: '1.125rem',
            letterSpacing: '0.02em',
            textTransform: 'none',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 8px 24px rgba(197, 232, 125, 0.3)'
                : '0 8px 24px rgba(124, 179, 66, 0.3)',
            transition: `all 0.3s ${springEasing}`,
            '&:hover': {
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.darker})`,
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 12px 32px rgba(197, 232, 125, 0.4)'
                  : '0 12px 32px rgba(124, 179, 66, 0.4)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          Continue to Equipment Upload
        </Button>
      </CardContent>
    </Card>
  );
}
